import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { get, post } from "@/lib/api";
import { useSession } from "@/context/SessionContext";
import { DemoCodeNote } from "@/pages/Doorway";
import { IllustrativeBadge } from "@/components/IllustrativeBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, SearchX, Clock, LifeBuoy, ArrowRight, KeyRound } from "lucide-react";

const ROLE_TEXT = { volunteer: "Volunteer", group_leader: "Group Leader" };

export default function InviteEntry() {
  const { token } = useParams();
  const nav = useNavigate();
  const { signIn } = useSession();
  const [inv, setInv] = useState(null);
  const [state, setState] = useState("loading");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [support, setSupport] = useState(false);

  useEffect(() => {
    get(`/core/invite/${token}`).then((d) => { setInv(d); setState("ok"); }).catch(() => setState("notfound"));
  }, [token]);

  if (state === "loading") return <Mid><Loader2 className="h-5 w-5 animate-spin" /> Loading your invitation…</Mid>;
  if (state === "notfound") return <Mid><div className="text-center"><SearchX className="mx-auto h-8 w-8 text-mill-muted" /><p className="mt-2 font-semibold text-mill-ink">Invitation not found</p><p className="text-sm text-mill-muted">This personal link is not valid.</p></div></Mid>;

  const blocked = ["revoked", "expired"].includes(inv.status);
  const roleName = ROLE_TEXT[inv.role] || "Volunteer";

  const start = async () => {
    const role = signIn(code, token);
    if (role !== "volunteer") { toast.error("Enter your access code: volunteer or v."); return; }
    setBusy(true);
    try {
      await post(`/core/invite/${token}/redeem`, {});
      toast.success("Your Mill Card is ready.");
      nav("/me");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Could not open your space.");
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-5 py-12 sm:px-8" data-testid="invite-entry">
      <IllustrativeBadge className="mb-5" />
      <div className="rounded-3xl border border-stone-200 bg-white p-7 sm:p-9">
        <p className="text-sm font-semibold uppercase tracking-[0.16em] text-mill-moss">Welcome to The Mill</p>
        <h1 className="mt-2 font-display text-3xl font-bold text-mill-ink">Hello {inv.display_name}</h1>
        <p className="mt-3 text-mill-muted">
          You've been invited to begin the <b className="text-mill-ink">{roleName} onboarding pathway</b>.
          {inv.team ? <> With the <b className="text-mill-ink">{inv.team}</b> team.</> : null}
        </p>
        <p className="mt-2 flex items-center gap-1.5 text-sm text-mill-muted"><Clock className="h-4 w-4" /> Estimated time: 10–15 minutes</p>

        {blocked ? (
          <p className="mt-6 rounded-xl bg-[#FEE2E2] px-4 py-3 text-sm text-[#991B1B]" data-testid="invite-blocked">This invitation is {inv.status} and can no longer be used. Please contact The Mill.</p>
        ) : (
          <>
            <div className="mt-6 rounded-2xl border border-stone-200 bg-mill-alt/40 p-4">
              <div className="flex items-center gap-2 text-mill-moss"><KeyRound className="h-4 w-4" /><p className="text-sm font-semibold text-mill-ink">Enter your access code to continue</p></div>
              <div className="mt-3 flex flex-wrap items-end gap-3">
                <Input value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && start()} placeholder="volunteer" className="w-48" data-testid="invite-code-input" />
                <Button className="h-10 gap-2 bg-mill-moss hover:bg-mill-moss-dark" onClick={start} disabled={busy} data-testid="invite-start">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Start onboarding <ArrowRight className="h-4 w-4" /></>}
                </Button>
              </div>
              <DemoCodeNote />
            </div>

            <button onClick={() => setSupport((s) => !s)} className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-mill-muted hover:text-mill-ink" data-testid="invite-support-toggle">
              <LifeBuoy className="h-4 w-4" /> Need a paper, assisted or in-person route?
            </button>
            {support && (
              <p className="mt-2 rounded-xl bg-white px-4 py-3 text-sm text-mill-muted ring-1 ring-stone-200" data-testid="invite-support-note">
                No problem — The Mill can support you in person or on paper. When you enter your space you can send a support
                request, or contact The Mill team directly and they'll help you complete onboarding.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Mid({ children }) { return <div className="mx-auto flex max-w-2xl items-center justify-center gap-2 px-5 py-24 text-mill-muted">{children}</div>; }
